<script setup lang="ts">
import { ref, watch, nextTick, computed, onMounted, onBeforeUnmount } from 'vue'
import { isTool, isNode } from '@/types'
import type { ChatItem, ChatMessage } from '@/types'
import MessageBubble from './MessageBubble.vue'
import ActivityGroup from './ActivityGroup.vue'
import TypingDots from './TypingDots.vue'

const props = defineProps<{
  items: ChatItem[]
  streaming?: boolean
}>()

// Show a pulsing "typing" bubble as the last row while a response is pending:
// the answer hasn't started yet (last turn is the user message, or the
// assistant bubble exists but is still empty and not an error).
const showTyping = computed(() => {
  if (!props.streaming) return false
  const lastMsg = [...props.items].reverse().find(i => !('type' in i)) as ChatMessage | undefined
  if (!lastMsg || lastMsg.role === 'user') return true
  return !lastMsg.content?.trim() && !lastMsg.error
})

const emit = defineEmits<{
  (e: 'retry'): void
  (e: 'feedback', payload: { message: ChatMessage; sentiment: 'up' | 'down' }): void
}>()

// Collapse consecutive node/tool activity items into a single expandable
// "system activity" group, while messages render as their own rows. This keeps
// the step-by-step detail available without spamming a long list of bubbles.
type RenderRow =
  | { kind: 'message'; key: string; message: ChatMessage }
  | { kind: 'activity'; key: string; activities: ChatItem[] }

const rows = computed<RenderRow[]>(() => {
  const out: RenderRow[] = []
  let buffer: ChatItem[] = []

  const flush = () => {
    if (buffer.length) {
      out.push({ kind: 'activity', key: `act-${buffer[0].id}`, activities: buffer })
      buffer = []
    }
  }

  for (const item of props.items) {
    if (isNode(item) || isTool(item)) {
      buffer.push(item)
    } else {
      flush()
      out.push({ kind: 'message', key: item.id, message: item as ChatMessage })
    }
  }
  flush()
  return out
})

const container = ref<HTMLElement | null>(null)
const content = ref<HTMLElement | null>(null)

// Sticky-scroll state: follow the newest bubble only while the user is at the
// bottom. When the user has scrolled up, freeze their position and surface a
// clickable "New messages" pill instead of yanking them to the bottom.
const atBottom = ref(true)
const showNewMessages = ref(false)
// Tight tolerance so an intentional scroll-up (even a few dozen px) counts as
// "not at bottom"; still absorbs subpixel/elastic-scroll rounding.
const BOTTOM_THRESHOLD = 24

function isNearBottom(): boolean {
  const el = container.value
  if (!el) return true
  return el.scrollHeight - el.scrollTop - el.clientHeight <= BOTTOM_THRESHOLD
}

function scrollToBottom(): void {
  const el = container.value
  if (!el) return
  // Instant (not smooth): smooth scrolling emits intermediate scroll events
  // that would flip atBottom false and flicker the pill.
  el.scrollTop = el.scrollHeight
  atBottom.value = true
  showNewMessages.value = false
}

function onScroll(): void {
  atBottom.value = isNearBottom()
  if (atBottom.value) showNewMessages.value = false
}

// Single multi-source watcher: fires on new items (length), typing indicator
// toggling, and streamed content growth of the last message. Follow the bottom
// only if the user was already there; otherwise surface the pill.
watch(
  [
    () => props.items.length,
    showTyping,
    () => {
      const last = props.items[props.items.length - 1]
      return last && !isTool(last) && !isNode(last) ? last.content : null
    },
  ],
  async () => {
    await nextTick()
    if (atBottom.value) {
      scrollToBottom()
    } else {
      showNewMessages.value = true
    }
  },
)

// Array identity swap (thread switch rebuilds items) -> treat as a fresh
// conversation and snap to the bottom, even if length/content are unchanged.
watch(
  () => props.items,
  async () => {
    atBottom.value = true
    showNewMessages.value = false
    await nextTick()
    scrollToBottom()
  },
)

// Content can grow after nextTick without any watched source changing (e.g.
// Highcharts rendering, images finishing load). Re-pin only while sticky.
let resizeObserver: ResizeObserver | null = null

onMounted(async () => {
  await nextTick()
  scrollToBottom()
  if (content.value && typeof ResizeObserver !== 'undefined') {
    resizeObserver = new ResizeObserver(() => {
      if (atBottom.value) scrollToBottom()
    })
    resizeObserver.observe(content.value)
  }
})

onBeforeUnmount(() => {
  resizeObserver?.disconnect()
  resizeObserver = null
})
</script>

<template>
  <div class="relative flex-1 min-h-0">
    <div ref="container" class="absolute inset-0 overflow-y-auto p-5" @scroll.passive="onScroll">
      <div ref="content" class="flex flex-col gap-3">
        <template v-for="row in rows" :key="row.key">
          <ActivityGroup v-if="row.kind === 'activity'" :activities="row.activities" />
          <MessageBubble
            v-else
            :message="row.message"
            @retry="emit('retry')"
            @feedback="emit('feedback', $event)"
          />
        </template>

        <!-- Pending response indicator: always the last bubble while streaming -->
        <div
          v-if="showTyping"
          class="self-start max-w-[85%] rounded-[14px_14px_14px_4px] border border-neutral-200 bg-white px-4 py-3"
        >
          <TypingDots :size="6" color-class="bg-neutral-400" />
        </div>
      </div>
    </div>
    <button
      v-if="showNewMessages"
      type="button"
      class="absolute bottom-4 left-1/2 -translate-x-1/2 z-10 rounded-full bg-neutral-800 text-white text-xs px-3 py-1.5 shadow-md hover:bg-neutral-700"
      @click="scrollToBottom"
    >
      New messages ↓
    </button>
  </div>
</template>
